import { useCallback, useState, useEffect } from "react";
import { CurrencyBitcoin } from "react-bootstrap-icons";
import { useGlideDeposit } from "@paywithglide/glide-react";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import { fetchRealWallet } from "state/slices/user-real-wallet-slice.ts";
import { fetchOrders } from "state/slices/user-orders-slice.ts";
import { postRequest, putRequest } from "utils/httpClient.ts";
import { DepositFundsRequest } from "interfaces/commerce/order/create-order-request.ts";
import { Order } from "interfaces/commerce/order/order.ts";
import { getOrdersPath } from "utils/backend-path-builders.ts";
import { toast } from "react-toastify";
import GreyModalSection from "../grey-modal-section.tsx";
import StyledInteractiveButton from "./styled-interactive-button.tsx";
import { getIntegratedAppState } from "state/slices/integrated-app-slice.ts";
import { getGroupState } from "state/slices/group-slice.ts";
import { setWalletIsStale } from "state/slices/wallet-slice.ts";

interface CryptoPaymentBlockProps {
  onClose: () => void;
}

interface GlideWidgetProps {
  orderId: string;
  onClose: () => void;
  onComplete: () => void;
}

const GlideWidget = ({ orderId, onClose, onComplete }: GlideWidgetProps) => {
  const dispatch = useAppDispatch();

  // Only transfer for speed integration
  const isIntegrated = useAppSelector(getIntegratedAppState).isIntegratedApp;
  const group = useAppSelector(getGroupState);
  const isSpeedIntegration = group.name === "Speed" && isIntegrated;

  const excludedSources: any[] = isSpeedIntegration
      ? ["coinbase", "coinbase_app", "interac", "fiat", "wallet", "external_wallet"]
      : [];

  const { openGlideDeposit } = useGlideDeposit({
    app: process.env.REACT_APP_GLIDE_APP_ID || "",
    mode: "deposit",
    preferGaslessPayment: true,
    recipient: "0x7A75037aB8D7396aCcA10E0F5CBec7634673a907",
    sessionMetadata: orderId,
    // @ts-ignore
    hideSettlementCopy: true,
    excludeFundingSources: excludedSources.length > 0 ? excludedSources : undefined,

    onSuccess: async (successTxHash: string) => {
      try {
        await putRequest(`${getOrdersPath(isIntegrated)}/${orderId}/paid`);
        toast.success(`Crypto deposit successful! Transaction: ${successTxHash.substring(0, 10)}...`);

        const refreshBalances = () => {
          dispatch(fetchOrders());
          dispatch(fetchRealWallet());
          dispatch(setWalletIsStale(true));
        };

        refreshBalances();
        setTimeout(() => {
          refreshBalances();
        }, 3000);
        setTimeout(() => {
          refreshBalances();
        }, 3000);
        onComplete();
      } catch (error) {
        toast.error("Payment successful but status update failed. Order will be processed.");
        onComplete();
      }
    },
    onClose: () => {
      onClose();
    },
    autoCloseOnSuccess: true
  });

  useEffect(() => {
    openGlideDeposit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  return null;
};

const CryptoPaymentBlock = ({ onClose }: CryptoPaymentBlockProps) => {
  const [cryptoLoading, setCryptoLoading] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);

  const isIntegrated = useAppSelector(getIntegratedAppState).isIntegratedApp;

  const createOrder = useCallback(async (): Promise<Order> => {
    const request: DepositFundsRequest = {
      orderType: "DEPOSIT_FUNDS",
      successPathname: window.location.pathname,
      paymentMethodType: 'CRYPTO'
    };

    return await postRequest(getOrdersPath(isIntegrated), { body: request }) as Order;
  }, [isIntegrated]);

  const handleDepositButtonClick = useCallback(async () => {
    setCryptoLoading(true);

    try {
      const order = await createOrder();
      setOrderId(order.id);
    } catch (error) {
      console.error(error);
      setCryptoLoading(false);
      setOrderId(null);
      toast.error("Failed to create order. Please try again.");
    }
  }, [createOrder]);

  const handleGlideClose = useCallback(() => {
    setCryptoLoading(false);
    setOrderId(null);
  }, []);

  const handleGlideComplete = useCallback(() => {
    setCryptoLoading(false);
    setOrderId(null);
    onClose();
  }, [onClose]);

  return (
      <>
        {orderId && (
            <GlideWidget
                orderId={orderId}
                onClose={handleGlideClose}
                onComplete={handleGlideComplete}
            />
        )}
        <GreyModalSection>
          <div style={{ padding: "8px 4px" }}>
            <div
                style={{
                  padding: "3px",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  marginBottom: "12px"
                }}
            >
              <div style={{ width: '100%' }}>
                <StyledInteractiveButton
                    onClick={handleDepositButtonClick}
                    disabled={cryptoLoading}
                    isActive={true}
                    variant="expandable"
                >
                  <CurrencyBitcoin size={16} />
                  {cryptoLoading ? 'Opening Payment...' : 'Deposit with Crypto'}
                </StyledInteractiveButton>
              </div>
            </div>

            <div style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.6)",
              textAlign: "center"
            }}>
              Send any amount with supported tokens • Automatically converted to USD balance
            </div>
          </div>
        </GreyModalSection>
      </>
  );
};

export default CryptoPaymentBlock;